/**
 * ImageEditingTools.tsx - Image-Specific Editing Tools Component
 *
 * This component provides specialized editing tools specifically designed
 * for image manipulation including filters, adjustments, and effects.
 *
 * Key Features:
 * - Color adjustment tools (brightness, contrast, saturation, hue)
 * - Filter presets and custom filter creation
 * - Image enhancement tools (sharpen, blur, noise reduction)
 * - Color correction and white balance
 * - Histogram display and analysis
 * - Batch processing capabilities for multiple images
 *
 * Adjustment Tools:
 * - Brightness: Lighten or darken the image
 * - Contrast: Adjust difference between light and dark areas
 * - Saturation: Control color intensity and vibrancy
 * - Hue: Shift color spectrum and tones
 * - Gamma: Adjust midtone brightness
 * - Exposure: Simulate camera exposure adjustments
 *
 * Filter Categories:
 * - Artistic: Oil painting, watercolor, sketch effects
 * - Vintage: Sepia, black and white, retro filters
 * - Enhancement: Sharpen, clarity, vibrance
 * - Creative: Vignette, tilt-shift, color splash
 *
 * Props:
 * - imageData: ImageData - Current image data for processing
 * - onImageChange: (newImageData: ImageData) => void - Image update handler
 * - selectedTool: string - Currently active editing tool
 *
 * Dependencies: Canvas API, image processing algorithms, filter libraries
 */

import React, { useState } from "react";
import styled from "styled-components";
import {
  Crop,
  ZoomIn,
  Scissors,
  RotateCw,
  FlipHorizontal,
  FlipVertical,
  Palette,
  Undo,
  Redo,
} from "lucide-react";
import { EditingTool } from "./EditingPanel";
import AIIcon from "../../../../../images/fundraiser/ai.svg";

const ToolsContainer = styled.div<{ isVertical: boolean }>`
  display: flex;
  flex-direction: ${(props) => (props.isVertical ? "column" : "row")};
  gap: ${(props) => (props.isVertical ? "8px" : "16px")};
  align-items: center;
  justify-content: center;
  width: 100%;
  position: relative;
  z-index: 1000;
`;

const ToolGroup = styled.div<{ isVertical: boolean }>`
  display: flex;
  flex-direction: ${(props) => (props.isVertical ? "column" : "row")};
  gap: ${(props) => (props.isVertical ? "4px" : "8px")};
  padding: 4px;
  background: ${(props) =>
    props.isVertical ? "transparent" : "rgba(255, 255, 255, 0.0)"};
  border-radius: 12px;
  border: none;
`;

const ToolButton = styled.button<{
  active?: boolean;
  disabled?: boolean;
  variant?: "primary" | "secondary";
  isVertical?: boolean;
}>`
  width: ${(props) => (props.isVertical ? "48px" : "44px")};
  height: ${(props) => (props.isVertical ? "48px" : "44px")};
  border: none;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  transition: all 0.2s ease;
  position: relative;

  ${(props) => {
    if (props.disabled) {
      return `
        background: #f1f5f9;
        color: #9ca3af;
        opacity: 0.5;
      `;
    } else if (props.active) {
      return `
        background: #3b82f6;
        color: #ffffff !important;
        box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
        
        img {
          filter: brightness(0) saturate(100%) invert(100%) sepia(0%) saturate(7496%) hue-rotate(86deg) brightness(118%) contrast(119%) !important;
        }
      `;
    } else if (props.variant === "primary") {
      return `
        background: #10b981;
        color: #ffffff;
        
        &:hover {
          background: #059669;
          transform: translateY(-1px);
        }
      `;
    } else if (props.variant === "secondary") {
      return `
        background: #ef4444;
        color: #ffffff;
        
        &:hover {
          background: #dc2626;
          transform: translateY(-1px);
        }
      `;
    } else {
      return `
        background: #f1f5f9;
        color: #64748b;
        
        &:hover {
          background: #e2e8f0;
          color: #1e293b;
          transform: translateY(-1px);
        }
      `;
    }
  }}

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  img {
    transition: filter 0.2s ease;
  }
`;

const Tooltip = styled.div<{ visible: boolean; isVertical: boolean }>`
  position: absolute;
  ${(props) => (props.isVertical ? "left: 100%;" : "bottom: 100%;")}
  ${(props) => (props.isVertical ? "top: 50%;" : "left: 50%;")}
  transform: ${(props) =>
    props.isVertical ? "translateY(-50%)" : "translateX(-50%)"};
  background: rgba(0, 0, 0, 0.9);
  color: #ffffff;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  z-index: 999999999;
  opacity: ${(props) => (props.visible ? 1 : 0)};
  visibility: ${(props) => (props.visible ? "visible" : "hidden")};
  transition: all 0.2s ease;
  pointer-events: none;
  margin-left: ${(props) => (props.isVertical ? "8px" : "0")};
  margin-bottom: ${(props) => (props.isVertical ? "0" : "8px")};

  &::after {
    content: "";
    position: absolute;
    ${(props) => (props.isVertical ? "right: 100%;" : "top: 100%;")}
    ${(props) => (props.isVertical ? "top: 50%;" : "left: 50%;")}
    transform: ${(props) =>
      props.isVertical ? "translateY(-50%)" : "translateX(-50%)"};
    border: 4px solid transparent;
    ${(props) =>
      props.isVertical
        ? "border-right-color: rgba(0, 0, 0, 0.9);"
        : "border-top-color: rgba(0, 0, 0, 0.9);"}
  }
`;

interface ImageEditingToolsProps {
  activeTool: EditingTool;
  onToolSelect: (tool: EditingTool) => void;
  onCropClick?: () => void;
  onRotate: () => void;
  onFlipH: () => void;
  onFlipV?: () => void;
  onRemoveBackground?: () => void;
  isProcessing: boolean;
  hasValidApiKey: boolean;
  zoom: number;
  onZoomChange: (zoom: number) => void;
  isVerticalLayout?: boolean;
  cropData?: {
    x: number;
    y: number;
    width: number;
    height: number;
  } | null;
}

export const ImageEditingTools: React.FC<ImageEditingToolsProps> = ({
  activeTool,
  onToolSelect,
  onCropClick,
  onRotate,
  onFlipH,
  onFlipV,
  onRemoveBackground,
  isProcessing,
  hasValidApiKey,
  zoom,
  onZoomChange,
  isVerticalLayout = false,
  cropData = null,
}) => {
  const handleToolClick = (tool: EditingTool) => {
    if (tool === "crop") {
      if (tool === activeTool) {
        // Deselect crop if already active
        onToolSelect("details");
      } else if (onCropClick) {
        onCropClick();
      }
    } else if (tool === activeTool) {
      // Deselect other tools
      onToolSelect("details");
    } else {
      onToolSelect(tool);
    }
  };

  return (
    <ToolsContainer isVertical={isVerticalLayout}>
      {/* Main Editing Tools */}
      <ToolGroup isVertical={isVerticalLayout}>
        <ToolButton
          active={activeTool === "crop"}
          onClick={() => handleToolClick("crop")}
          disabled={isProcessing}
          isVertical={isVerticalLayout}
          title="Crop"
        >
          <Crop size={20} />
        </ToolButton>

        <ToolButton
          active={activeTool === "zoom"}
          onClick={() => handleToolClick("zoom")}
          disabled={isProcessing}
          isVertical={isVerticalLayout}
          title={cropData ? "Zoom (crop-constrained)" : "Zoom"}
        >
          <ZoomIn size={20} />
          {cropData && (
            <div
              style={{
                position: "absolute",
                top: "2px",
                right: "2px",
                width: "8px",
                height: "8px",
                background: "#10b981",
                borderRadius: "50%",
                border: "1px solid #ffffff",
              }}
            />
          )}
        </ToolButton>

        <ToolButton
          active={activeTool === "filters"}
          onClick={() => handleToolClick("filters")}
          disabled={isProcessing}
          isVertical={isVerticalLayout}
          title={"Filters"}
        >
          <Palette size={20} />
        </ToolButton>

        <ToolButton
          active={activeTool === "rotate"}
          onClick={() => handleToolClick("rotate")}
          disabled={isProcessing}
          isVertical={isVerticalLayout}
          title="Rotate"
        >
          <RotateCw size={20} />
        </ToolButton>

        <ToolButton
          active={activeTool === "flip"}
          onClick={() => handleToolClick("flip")}
          disabled={isProcessing}
          isVertical={isVerticalLayout}
          title="Flip"
        >
          <FlipHorizontal size={20} />
        </ToolButton>

        <ToolButton
          active={activeTool === "ai"}
          onClick={() => handleToolClick("ai")}
          disabled={isProcessing}
          isVertical={isVerticalLayout}
          title="AI"
        >
          <img
            src={AIIcon}
            alt="AI Edit"
            width={20}
            height={20}
            style={{
              filter:
                activeTool === "ai"
                  ? "brightness(0) saturate(100%) invert(100%) sepia(0%) saturate(7496%) hue-rotate(86deg) brightness(118%) contrast(119%)"
                  : "brightness(0) saturate(100%) invert(44%) sepia(11%) saturate(572%) hue-rotate(202deg) brightness(91%) contrast(86%)",
            }}
          />
        </ToolButton>
      </ToolGroup>
    </ToolsContainer>
  );
};
